import axios from 'axios';
import { resolvePlatformApiBaseUrl } from '../utils/platformApiBaseUrl';
import type { InjectGateResult } from '../utils/injectGate';

const BASE_URL = resolvePlatformApiBaseUrl();

// Create a separate client for OBP AI requests without credentials
const obpAiClient = axios.create({
  baseURL: BASE_URL,
  timeout: 30000,
  withCredentials: false, // AI service doesn't need authentication
  headers: {
    'Content-Type': 'application/json',
  },
});

export type UIListing = {
  title?: string;
  price?: string | number;
  url?: string;
  image?: string;
  description?: string;
  seller?: string;
  product?: string;
  marketplace?: string;
  brand?: string;
  images?: string[];
  catalog_products?: string[];
};

function parsePriceToNumber(price: string | number | undefined): number | undefined {
  if (price === undefined || price === null) return undefined;
  if (typeof price === 'number') return price;
  const cleaned = price.replace(/[^0-9.,-]/g, '').replace(',', '.');
  const parsed = parseFloat(cleaned);
  return isNaN(parsed) ? undefined : parsed;
}

function extractDomain(url?: string): string | undefined {
  try {
    if (!url) return undefined;
    const u = new URL(url);
    return u.hostname;
  } catch (_) {
    return undefined;
  }
}

export type InjectGateRequestOptions = {
  /** Default 60s. Auto-process uses a shorter bound so a slow LLM cannot freeze the queue. */
  timeoutMs?: number;
  /** Skip listing photos (text-only). Faster and avoids vision content-policy / fetch stalls. */
  skipImages?: boolean;
  signal?: AbortSignal;
};

export function listingImagesForGate(listing: UIListing): string[] {
  return (
    listing.images && listing.images.length > 0
      ? listing.images
      : listing.image
        ? [listing.image]
        : []
  ).filter(Boolean);
}

export function isInjectGateAbortError(err: unknown): boolean {
  return (
    axios.isCancel(err) ||
    (err instanceof Error && (err.name === 'AbortError' || err.name === 'CanceledError'))
  );
}

export function injectGateErrorMessage(err: unknown, timeoutMs: number): string {
  if (axios.isAxiosError(err) && err.code === 'ECONNABORTED') {
    return `Inject gate timed out after ${timeoutMs}ms`;
  }
  if (isInjectGateAbortError(err)) {
    return `Inject gate timed out after ${timeoutMs}ms`;
  }
  if (err instanceof Error && err.message) return err.message;
  return 'Inject gate request failed';
}

export async function evaluateInjectGate(
  listing: UIListing,
  options: InjectGateRequestOptions = {}
): Promise<InjectGateResult> {
  const timeoutMs = options.timeoutMs ?? 60000;
  const payload = {
    declared_brand: (listing.brand && listing.brand.trim()) || '',
    declared_product: listing.product || undefined,
    catalog_products: (listing.catalog_products || []).filter(Boolean),
    title: listing.title || 'Untitled',
    description: listing.description || undefined,
    seller: listing.seller || undefined,
    price:
      listing.price !== undefined && listing.price !== null ? String(listing.price) : undefined,
    url: listing.url || undefined,
    marketplace: listing.marketplace || undefined,
    images: options.skipImages ? [] : listingImagesForGate(listing),
  };

  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), timeoutMs);
  const onParentAbort = () => controller.abort();
  options.signal?.addEventListener('abort', onParentAbort);
  if (options.signal?.aborted) {
    clearTimeout(timer);
    controller.abort();
  }

  try {
    const response = await obpAiClient.post('/api/v1/ai/inject-gate', payload, {
      timeout: timeoutMs,
      signal: controller.signal,
    });
    const data = response?.data || {};
    return {
      brand: data.brand || 'warn',
      product: data.product || 'warn',
      infringement: data.infringement || 'warn',
      invalid: Boolean(data.invalid),
      decision: data.decision || 'hold',
      summary: data.summary || 'Inject gate completed',
      reasons: Array.isArray(data.reasons) ? data.reasons.map(String) : [],
      selected_product: data.selected_product || null,
      product_corrected: Boolean(data.product_corrected),
      brand_ip_merchandise: Boolean(data.brand_ip_merchandise),
      article_category:
        typeof data.article_category === 'string' && data.article_category.trim()
          ? data.article_category.trim()
          : null,
    };
  } catch (err) {
    if (options.signal?.aborted) {
      throw err;
    }
    if (isInjectGateAbortError(err) || (axios.isAxiosError(err) && err.code === 'ECONNABORTED')) {
      throw new Error(`Inject gate timed out after ${timeoutMs}ms`);
    }
    throw err;
  } finally {
    clearTimeout(timer);
    options.signal?.removeEventListener('abort', onParentAbort);
  }
}

export async function analyzeListingWithAI(
  listing: UIListing,
  idHint: string
): Promise<{ assessment: string; analysis: any }> {
  // Build payload matching OBP AI Listing model
  const nowIso = new Date().toISOString();
  const listingPayload: any = {
    _id: idHint,
    url: listing.url || undefined,
    domain: extractDomain(listing.url),
    title: listing.title || 'Untitled',
    body: listing.description || undefined,
    brand: (listing.brand && listing.brand.trim()) || 'Unknown',
    product: listing.product || undefined,
    platform: listing.marketplace || undefined,
    price: parsePriceToNumber(listing.price),
    created: nowIso,
    images: (listing.images && listing.images.length > 0
      ? listing.images
      : listing.image
        ? [listing.image]
        : []
    ).filter(Boolean),
  };

  try {
    const response = await obpAiClient.post('/api/v1/ai/analyze', {
      listing: listingPayload,
    });
    const analysis = response?.data?.analysis ?? null;
    const assessment: string = analysis?.assessment || 'UNKNOWN';
    return { assessment, analysis };
  } catch (error: any) {
    const msg = error?.response?.data?.detail || error?.message || 'AI analysis failed';
    throw new Error(msg);
  }
}

export default { analyzeListingWithAI, evaluateInjectGate };
